import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import Button from 'components/Button';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import appMessages from 'features/App/App.messages';
import { classNameWithModifiers } from 'helpers/class-name';

import './Confirm.scss';

const defaultRender = ({ title, body, buttons }) => (
  <>
    {title}
    {body}
    {buttons}
  </>
);

defaultRender.propTypes = {
  title: PropTypes.node,
  body: PropTypes.node,
  buttons: PropTypes.node,
};

const Confirm = props => {
  const {
    title,
    subtitle,
    cancelNode,
    cancelStyle = 'tertiary',
    acceptNode,
    acceptStyle,
    acceptDisabled,
    acceptWaiting,
    hasCloseButton,
    hideButtonBorder,
    onRequestClose,
    onCancel,
    onConfirm,
    children,
    className = '',
    cypressId = 'confirm-dialog',
    size = 'standard',
  } = props;

  const intl = useIntl();
  const closeAriaLabel = intl.formatMessage(appMessages.close);
  const handleRequestClose = onRequestClose ?? onCancel;

  const withMods = base =>
    classNameWithModifiers(base, {
      standard: size === 'standard',
      smMedium: size === 'smMedium',
      medium: size === 'medium',
      large: size === 'large',
      extended: size === 'extended',
      complex: size === 'complex',
      hideButtonBorder,
    });
  const render = typeof children === 'function' ? children : defaultRender;
  const titleNode = (
    <div className={withMods('confirm__title-container')}>
      <h4 className={withMods('confirm__title')}>
        {title}
        <When predicate={hasCloseButton}>
          <button className="confirm__close-button" type="button" onClick={handleRequestClose}>
            <Isvg src={ICONS.cross} aria-label={closeAriaLabel} />
          </button>
        </When>
      </h4>

      {subtitle && <h5 className={withMods('confirm__subtitle')}>{subtitle}</h5>}
    </div>
  );
  const bodyClass = withMods('confirm__body');
  const bodyNode = children ? <div className={bodyClass}>{children}</div> : null;
  // Audit-checked: consumers that need Cancel to run custom close logic now pass that logic through onCancel.
  const buttonsNode = (
    <div className={withMods('confirm__buttons-wrap')}>
      {cancelNode && (
        <Button
          className="confirm__button confirm__cancel"
          styled={cancelStyle}
          type={Button.TYPES.BUTTON}
          onClick={onCancel}>
          {cancelNode}
        </Button>
      )}
      {acceptNode && (
        <Button
          className="confirm__button confirm__accept"
          styled={acceptStyle ?? (size !== 'complex' ? 'primary' : 'primary-outline')}
          type={Button.TYPES.BUTTON}
          onClick={onConfirm}
          disabled={acceptDisabled}
          waiting={acceptWaiting}>
          {acceptNode}
        </Button>
      )}
    </div>
  );

  return (
    <div className={classNames(withMods('confirm'), className)} data-cypress={cypressId}>
      {render({
        title: titleNode,
        body: bodyNode,
        buttons: buttonsNode,
        bodyClass,
      })}
    </div>
  );
};

Confirm.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  cancelNode: PropTypes.node,
  cancelStyle: PropTypes.string,
  acceptNode: PropTypes.node,
  acceptStyle: PropTypes.string,
  acceptDisabled: PropTypes.bool,
  acceptWaiting: PropTypes.bool,
  hasCloseButton: PropTypes.bool,
  hideButtonBorder: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  className: PropTypes.string,
  cypressId: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  size: PropTypes.string,
};

Confirm.defaultProps = {
  acceptDisabled: false,
};

export default Confirm;
